import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IFilters } from '../interfaces/filters.model';
import { Observable } from 'rxjs';
import {
  Advertiser,
  Discussion,
  DiscussionPost,
  LineItem,
  Order,
} from '../domain/campaigns.model';
import { Page } from '../domain/page.model';
import { urlJoin } from 'url-join-ts';
import { environment } from '../../../environments/environment';
import { HttpUtils } from '../utils/http-utils';
import { CreativeLibrary } from '../domain/creative-library.model';
import { Utils } from '../utils/utils';
import { ActiveInactiveArchived } from '../enums/active-inactive-archived.enum';
import { ProcessingQueue } from '../domain/creative-studio.model';
import { PauseAdCreateDto } from '../../modules/campaigns/pages/add-creative-dialog/creative-pause-settings/creative-pause-settings.form';
import { SlateCreateDto } from '../../modules/campaigns/pages/add-creative-dialog/creative-slate-settings/creative-slate-settings.form';
import { MixpanelService } from './mixpanel.service';
import { MixpanelEvents, mixpanelCreativeTypeFromLibrary } from '../analytics/mixpanel-events';
import { withMixpanelOutcome } from '../analytics/mixpanel-tracking.utils';

@Injectable({
  providedIn: 'root',
})
export class CampaignsService {
  private readonly endpoint = 'api/campaign';

  constructor(
    private httpClient: HttpClient,
    private mixpanel: MixpanelService
  ) {}

  //#region LineItems
  public getLineItemsList(): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.serviceUrl}/api/campaign/lineitem`
    );
  }

  public getLineItemsListFiltered(filter?: any): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.serviceUrl}/api/campaign/lineitem`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  public getLineItemsListForCreativeId(filter?: any): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.serviceUrl}/api/campaign/lineitem`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  public addLineItem(data: LineItem): Observable<any> {
    return this.httpClient
      .post<LineItem>(
        urlJoin(environment.serviceUrl, this.endpoint, 'lineitem'),
        data
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.LineItemCreated,
          failureEvent: MixpanelEvents.LineItemCreateFailed,
          successProps: (created) => ({
            line_item_id: (created as LineItem)?.id,
            line_item_name: (created as LineItem)?.name,
            line_item_type: (created as LineItem)?.lineItemType,
            campaign_id: (created as LineItem)?.order?.id,
            campaign_name: (created as LineItem)?.order?.name,
            advertiser_id: (created as LineItem)?.order?.advertiser?.id,
            advertiser_name: (created as LineItem)?.order?.advertiser?.name,
          }),
          failureProps: () => ({
            line_item_name: data?.name,
            line_item_type: data?.lineItemType,
            campaign_id: data?.order?.id,
          }),
        })
      );
  }

  public duplicateLineItem(data: LineItem, originalLineItemId: string): Observable<any> {
    return this.httpClient
      .post<LineItem>(
        urlJoin(environment.serviceUrl, this.endpoint, 'lineitem'),
        { ...data, originalLineItemId }
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.LineItemCreated,
          failureEvent: MixpanelEvents.LineItemCreateFailed,
          successProps: (created) => ({
            line_item_id: (created as LineItem)?.id,
            line_item_name: (created as LineItem)?.name,
            line_item_type: (created as LineItem)?.lineItemType,
            campaign_id: (created as LineItem)?.order?.id,
            campaign_name: (created as LineItem)?.order?.name,
            advertiser_id: (created as LineItem)?.order?.advertiser?.id,
            advertiser_name: (created as LineItem)?.order?.advertiser?.name,
            duplicated_from: originalLineItemId,
          }),
          failureProps: () => ({
            line_item_name: data?.name,
            duplicated_from: originalLineItemId,
          }),
        })
      );
  }

  public updateLineItem(data: LineItem): Observable<any> {
    return this.httpClient.put<LineItem>(
      urlJoin(`${environment.serviceUrl}/api/campaign/lineitem/${data.id}`),
      data
    );
  }

  public getLineItemByID(id: string): Observable<LineItem> {
    return this.httpClient.get<LineItem>(
      `${environment.serviceUrl}/api/campaign/lineitem/${id}`
    );
  }

  public getAvailableProductSets(dcoCreativeIds: string[]): Observable<{ productSetsByCreative: Record<string, string[]> }> {
    return this.httpClient.post<{ productSetsByCreative: Record<string, string[]> }>(
      `${environment.serviceUrl}/api/campaign/dco/creative/available-product-sets`,
      { dcoCreativeIds }
    );
  }

  public getLineItemsStatuses(
    filters: IFilters
  ): Observable<{ [key: string]: number }> {
    return this.httpClient.get<{ [key: string]: number }>(
      urlJoin(environment.serviceUrl, this.endpoint, 'lineitem/statuses'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public getLineItemsHealthStatuses(
    filters: IFilters
  ): Observable<{ [key: string]: number }> {
    return this.httpClient.get<{ [key: string]: number }>(
      urlJoin(environment.serviceUrl, this.endpoint, 'lineitem/health-statuses'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public refreshAllHealthStatuses(): Observable<any> {
    return this.httpClient.get<any>(
      urlJoin(environment.serviceUrl, this.endpoint, 'lineitem/refresh-all-health-statuses')
    );
  }

  public getLineItemsDeliveryStatuses(
    filters: IFilters
  ): Observable<{ [key: string]: number }> {
    return this.httpClient.get<{ [key: string]: number }>(
      urlJoin(
        environment.serviceUrl,
        this.endpoint,
        'lineitem/delivery-statuses'
      ),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public extendLineItem(lineItemId: string, data: LineItem): Observable<any> {
    return this.httpClient.post<LineItem>(
      urlJoin(
        environment.serviceUrl,
        this.endpoint,
        `lineitem/${lineItemId}/extend`
      ),
      data
    );
  }

  public updateLineItemStatus(
    id: string,
    status: ActiveInactiveArchived
  ): Observable<any> {
    return this.httpClient.put<LineItem>(
      urlJoin(
        `${environment.serviceUrl}/api/campaign/lineitem/${id}/${status}`
      ),
      {}
    );
  }

  //#endregion

  //#region Orders
  public getOrdersList(filter?: any): Observable<Page<Order>> {
    return this.httpClient.get<Page<Order>>(
      `${environment.serviceUrl}/api/campaign/order`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  public getOrderByID(id: string): Observable<Order> {
    return this.httpClient.get<Order>(
      `${environment.serviceUrl}/api/campaign/order/${id}`
    );
  }

  public createOrder(data: Order): Observable<any> {
    return this.httpClient
      .post<any>(`${environment.serviceUrl}/api/campaign/order`, data)
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CampaignCreated,
          failureEvent: MixpanelEvents.CampaignCreateFailed,
          successProps: (created) => ({
            campaign_id: created?.id,
            campaign_name: created?.name,
            advertiser_id: created?.advertiser?.id,
            advertiser_name: created?.advertiser?.name,
          }),
          failureProps: () => ({
            campaign_name: data?.name,
            advertiser_id: data?.advertiser?.id,
          }),
        })
      );
  }

  public updateOrder(request: Order): Observable<any> {
    return this.httpClient.put<any>(
      `${environment.serviceUrl}/api/campaign/order/${request.id}`,
      request
    );
  }

  public getOrdersStatuses(
    filters?: IFilters
  ): Observable<{ [key: string]: number }> {
    return this.httpClient.get<{ [key: string]: number }>(
      urlJoin(environment.serviceUrl, this.endpoint, 'order/statuses'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public createDiscussion(
    data: Discussion,
    orderId: string
  ): Observable<Discussion> {
    return this.httpClient.post<Discussion>(
      `${environment.serviceUrl}/api/campaign/order/${orderId}/discussion`,
      data
    );
  }

  public getDiscussionById(
    discussionId: string,
    orderId: string
  ): Observable<Discussion> {
    return this.httpClient.get<Discussion>(
      `${environment.serviceUrl}/api/campaign/order/${orderId}/discussion/${discussionId}`
    );
  }

  public getDiscussions(orderId: string): Observable<Discussion[]> {
    return this.httpClient.get<Discussion[]>(
      `${environment.serviceUrl}/api/campaign/order/${orderId}/discussions`
    );
  }

  public createPost(
    data: DiscussionPost,
    discussionId: string,
    orderId: string
  ): Observable<DiscussionPost> {
    return this.httpClient.post<DiscussionPost>(
      `${environment.serviceUrl}/api/campaign/order/${orderId}/discussion/${discussionId}/post`,
      data
    );
  }

  public discussionSeen(
    discussionId: string,
    orderId: string
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.serviceUrl}/api/campaign/order/${orderId}/discussion/${discussionId}/seen`,
      {}
    );
  }

  public uploadCampaignsFile(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(
      `${environment.serviceUrl}/api/campaign/file-upload`,
      formData
    );
  }

  //#endregion

  //#region Advertiser
  public getAdvertiserList(filter: any): Observable<Page<Advertiser>> {
    !filter?.size ? (filter.size = 999) : null;
    return this.httpClient.get<Page<Advertiser>>(
      `${environment.serviceUrl}/api/campaign/advertiser`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  public getAdvertiserByID(id: string): Observable<Advertiser> {
    return this.httpClient.get<Advertiser>(
      `${environment.serviceUrl}/api/campaign/advertiser/${id}`
    );
  }

  public createAdvertiser(data: Advertiser): Observable<Advertiser> {
    return this.httpClient.post<Advertiser>(
      `${environment.serviceUrl}/api/campaign/advertiser`,
      data
    );
  }

  public updateAdvertiser(data: Advertiser): Observable<Advertiser> {
    return this.httpClient.put<Advertiser>(
      `${environment.serviceUrl}/api/campaign/advertiser/${data.id}`,
      data
    );
  }

  public getSimilarNamedAdvertiser(searchTerm: string): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.serviceUrl}/api/campaign/advertiser/search/similar`,
      {
        params: HttpUtils.getHttpParams({ searchTerm, threshold: 0.5 }),
      }
    );
  }
  //#endregion

  //#region Creatives
  public getCreativeLibraryList(
    filters: IFilters
  ): Observable<Page<CreativeLibrary>> {
    return this.httpClient.get<Page<CreativeLibrary>>(
      urlJoin(environment.serviceUrl, this.endpoint, 'creative'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public createCreativeLibrary(
    data: CreativeLibrary
  ): Observable<CreativeLibrary> {
    const formData: FormData = new FormData();
    formData.append('name', data.name);
    formData.append('type', data.type);
    formData.append('ivtEnabled', data.ivtEnabled.toString());
    if (data.ivtEnabled) formData.append('ivtType', data.ivtType);
    if (!Utils.isNullOrUndefined(data.ivtIntegrationType)) {
      formData.append('ivtIntegrationType', data.ivtIntegrationType);
    }
    if (!Utils.isNullOrUndefined(data.thirdPartyName)) {
      formData.append('thirdPartyName', data.thirdPartyName);
    }
    if (!Utils.isNullOrUndefined(data.thirdPartyId)) {
      formData.append('thirdPartyId', data.thirdPartyId);
    }
    formData.append('execution', data.execution);
    if (!Utils.isNullOrUndefined(data.watermarkEnabled)) {
      formData.append('watermarkEnabled', data.watermarkEnabled.toString());
    }
    if (!Utils.isNullOrUndefined(data.mute)) {
      formData.append('mute', data.mute.toString());
    }
    formData.append('duration', data.duration.toString());
    formData.append('advertiserId', data.advertiserId);
    if (data.video) {
      formData.append('creative', data.video.name);
      formData.append('video', data.video);
    } else if (data.vastType) {
      formData.append('vastType', data.vastType);
      if (data.vastXml) {
        formData.append('vastXml', data.vastXml);
      }
      if (data.vastUrl) {
        formData.append('vastUrl', data.vastUrl);
      }
    }
    if (data.creativeDesign) {
      formData.append('creativeDesign', data.creativeDesign);
    }
    if (data.orders) {
      data.orders.forEach((order, index) => {
        formData.append('order', order.id);
      });
    }
    return this.httpClient
      .post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, 'creative'),
        formData
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CreativeCreated,
          failureEvent: MixpanelEvents.CreativeCreateFailed,
          successProps: (created) => ({
            creative_type: mixpanelCreativeTypeFromLibrary(data.type),
            creative_id: created?.id,
            channel: 'library',
          }),
          failureProps: () => ({
            creative_type: mixpanelCreativeTypeFromLibrary(data.type),
            channel: 'library',
          }),
        })
      );
  }

  public createPauseAdCreative(data: PauseAdCreateDto): Observable<CreativeLibrary> {
    let request$: Observable<CreativeLibrary>;

    if (data.creative) {
      const formData: FormData = new FormData();

      const dto = {
        name: data.name,
        advertiser: { id: data.advertiserId },
        execution: data.execution,
        type: data.type,
        adspEnabled: data.adspEnabled ?? false
      };

      formData.append(
        'dto',
        new Blob([JSON.stringify(dto)], { type: 'application/json' })
      );
      formData.append('assetFile', data.creative);

      request$ = this.httpClient.post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, '/dco/creative'),
        formData
      );
    } else {
      const req = { ...data, duration: 15, advertiser: { id: data.advertiserId } };

      request$ = this.httpClient.post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, '/dco/creative'),
        req
      );
    }

    return request$.pipe(
      withMixpanelOutcome(this.mixpanel, {
        successEvent: MixpanelEvents.CreativeCreated,
        failureEvent: MixpanelEvents.CreativeCreateFailed,
        successProps: (created) => ({
          creative_type: 'PAUSE_AD',
          creative_id: created?.id,
          channel: 'library',
        }),
        failureProps: () => ({
          creative_type: 'PAUSE_AD',
          channel: 'library',
        }),
      })
    );
  }

  public createSlateCreative(data: SlateCreateDto): Observable<CreativeLibrary> {
    let request$: Observable<CreativeLibrary>;

    if (data.creative) {
      const formData: FormData = new FormData();

      const dto = {
        name: data.name,
        advertiser: { id: data.advertiserId },
        execution: data.execution,
        type: data.type
      };

      formData.append(
        'dto',
        new Blob([JSON.stringify(dto)], { type: 'application/json' })
      );
      formData.append('assetFile', data.creative);

      request$ = this.httpClient.post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, '/dco/creative'),
        formData
      );
    } else {
      const req = { ...data, advertiser: { id: data.advertiserId } };

      request$ = this.httpClient.post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, '/dco/creative'),
        req
      );
    }

    return request$.pipe(
      withMixpanelOutcome(this.mixpanel, {
        successEvent: MixpanelEvents.CreativeCreated,
        failureEvent: MixpanelEvents.CreativeCreateFailed,
        successProps: (created) => ({
          creative_type: 'SLATE',
          creative_id: created?.id,
          channel: 'library',
        }),
        failureProps: () => ({
          creative_type: 'SLATE',
          channel: 'library',
        }),
      })
    );
  }

  public updateCreativeLibrary(
    creativeLibrary: CreativeLibrary
  ): Observable<CreativeLibrary> {
    return this.httpClient
      .put<CreativeLibrary>(
        urlJoin(
          environment.serviceUrl,
          this.endpoint,
          'creative',
          creativeLibrary.id
        ),
        creativeLibrary
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CreativeUpdated,
          failureEvent: MixpanelEvents.CreativeUpdateFailed,
          successProps: () => ({
            creative_id: creativeLibrary.id,
            creative_type: mixpanelCreativeTypeFromLibrary(creativeLibrary.type),
            channel: 'library',
          }),
          failureProps: () => ({
            creative_id: creativeLibrary.id,
            creative_type: mixpanelCreativeTypeFromLibrary(creativeLibrary.type),
            channel: 'library',
          }),
        })
      );
  }

  public updateCreativeLibraryStatus(
    id: string,
    status: ActiveInactiveArchived
  ): Observable<any> {
    return this.httpClient
      .put<LineItem>(
        urlJoin(
          `${environment.serviceUrl}/api/campaign/creative/${id}/${status}`
        ),
        {}
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CreativeStatusChanged,
          failureEvent: MixpanelEvents.CreativeStatusChangeFailed,
          successProps: () => ({
            creative_id: id,
            status: String(status),
            channel: 'library',
          }),
          failureProps: () => ({
            creative_id: id,
            status: String(status),
            channel: 'library',
          }),
        })
      );
  }

  public getCreativesStatuses(
    filters: IFilters
  ): Observable<{ [key: string]: number }> {
    return this.httpClient.get<{ [key: string]: number }>(
      urlJoin(environment.serviceUrl, this.endpoint, 'creative/statuses'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public checkConnectedLineItemForStandard(creativeId: string) {
    return this.httpClient.get<any>(
      urlJoin(
        environment.serviceUrl,
        this.endpoint,
        `/creative/${creativeId}/lineitem`
      )
    );
  }

  public checkConnectedLineItem(creativeId: string) {
    return this.httpClient.get<any>(
      urlJoin(
        environment.serviceUrl,
        this.endpoint,
        `/dco/creative/${creativeId}/lineitem`
      )
    );
  }

  public reprocessStandardCreative(creativeId: string): Observable<any> {
    return this.httpClient.put<LineItem>(
      urlJoin(
        `${environment.serviceUrl}/api/campaign/creative/${creativeId}/reprocess`
      ),
      {}
    );
  }

  public getProcessingQueue(filter): Observable<ProcessingQueue[]> {
    return this.httpClient.get<ProcessingQueue[]>(
      `${environment.serviceUrl}/api/mixer/queue-estimate`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }
  //#endregion

  //#region DISPLAY Creatives
  public getDisplayCreativeLibraryList(
    filters: IFilters
  ): Observable<Page<CreativeLibrary>> {
    return this.httpClient.get<Page<CreativeLibrary>>(
      urlJoin(environment.serviceUrl, this.endpoint, 'display-creative'),
      {
        params: HttpUtils.getHttpParams(filters),
      }
    );
  }

  public createDisplayCreativeLibrary(
    data: CreativeLibrary,
    width: string,
    height: string
  ): Observable<CreativeLibrary> {
    const formData: FormData = new FormData();
    formData.append('name', data.name);
    formData.append('creative', data.image.name);
    formData.append('image', data.image);
    formData.append('width', width);
    formData.append('height', height);
    return this.httpClient
      .post<CreativeLibrary>(
        urlJoin(environment.serviceUrl, this.endpoint, 'display-creative'),
        formData
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CreativeCreated,
          failureEvent: MixpanelEvents.CreativeCreateFailed,
          successProps: (created) => ({
            creative_type: 'DISPLAY',
            creative_id: created?.id,
            channel: 'library',
          }),
          failureProps: () => ({
            creative_type: 'DISPLAY',
            channel: 'library',
          }),
        })
      );
  }

  public updateDisplayCreativeLibrary(
    creativeLibrary: CreativeLibrary
  ): Observable<CreativeLibrary> {
    return this.httpClient
      .put<CreativeLibrary>(
        urlJoin(
          environment.serviceUrl,
          this.endpoint,
          'display-creative',
          creativeLibrary.id
        ),
        creativeLibrary
      )
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.CreativeUpdated,
          failureEvent: MixpanelEvents.CreativeUpdateFailed,
          successProps: () => ({
            creative_id: creativeLibrary.id,
            creative_type: 'DISPLAY',
            channel: 'library',
          }),
          failureProps: () => ({
            creative_id: creativeLibrary.id,
            creative_type: 'DISPLAY',
            channel: 'library',
          }),
        })
      );
  }
  //#endregion
}
